export const formV2BlockDocumentationMarkdown = `
### What does this block do?

The Form Block allows healthcare administrators to deliver customized patient forms as part of a workflow and, optionally, wait for the resulting form set before continuing the flow — either until it reaches a terminal state (Completed or Expired), or, in completion mode, requiring it to be Completed (otherwise the block errors). A single block can both add form templates to a patient and (when configured) block the flow until the form set is finished, replacing the older pattern of pairing a separate **Wait for Form Completion** block. The block can be configured with dynamic parameters to personalize parts of the form, and scheduling and auto-export options control post-form completion behavior.

If the entity already has an uncompleted form set for the requested templates (e.g. one added by an earlier block or a different flow), the Form Block does **not** create a duplicate — it reuses the existing form set and subscribes the current flow to its terminal-state signal. When the form set finally completes (or expires), every flow that subscribed receives the signal and can proceed.

### When to use it?

- Use this block when you need to deliver patient-facing forms for data capture, such as pre-visit questionnaires, consent forms, or follow-up surveys.
- Employ it in workflows where forms must be dynamically tailored with parameters (e.g., pre-filled patient info or appointment details).
- Use it when downstream steps need to branch on the form set's terminal status, without wiring a separate **Wait for Form Completion** block.
- Integrate this block when scheduled or automatic export of form responses is required for further processing (such as integration with EHR systems).
- Use it when the form set should be attached to an entity other than the one that started the flow (e.g., a specific encounter).

### Example Flows

1. **Pre-Visit Survey Distribution (wait for terminal status):**  
   After scheduling an appointment, the flow triggers the Form Block in **Wait for formset to reach any terminal status** mode with a 2-day timeout. Dynamic parameters are used to insert appointment time and patient name. Downstream steps branch on the resulting \`formSet.status\` (Completed vs Expired); if the form set hasn't reached a terminal state within the timeout, the block errors instead of continuing. On form completion, auto-export functionality sends the responses to a data repository.

2. **Consent and Compliance Collection (fire-and-forget):**  
   In a consent workflow, the Form Block delivers legal or consent forms to a patient in **No wait** mode so the flow continues immediately. Multiple form templates can be attached as needed. Once the patient completes the form, the responses are automatically exported for compliance documentation by a downstream process.

3. **Follow-Up Feedback Request with Override Entity:**  
   Post-discharge, the block is used to send a survey gathering feedback on a specific encounter. **Override Entity** attaches the form set to that encounter rather than the entity that started the flow. Dynamic key/value parameters customize the survey based on the patient's treatment details, and a refresh interval can ensure that the same form isn't resent too frequently.

## How to Configure This Block

### **1. Form Selection**  
- **Description:** In the Configuration tab, locate the Form header with its purple user/profile icon.  
- **Steps:**  
   1. In the "Add form" section, use the search field ("Search from existing forms") to find and select form templates. **At least one form template is required.**  
   2. Helper text reminds you: "Select from existing forms or create a new form" – clicking the link opens the form creation interface at:  
      [Form Template Builder](https://app.notablehealth.com/staff/app#/previsit-form-templates).

### **2. Notification Scheme Override (optional)**
- **Description:** Below the form selection, the **Notification scheme override** picker lets you swap the scheme that will be used for outreach about this form set.
- **Steps:**
   1. Leave empty to fall back to the scheme configured on each form template (default).
   2. Pick a scheme to override for every form created by this block — useful when the same form template is being used in a flow that needs a different cadence/content than its default scheme.
   3. The override is persisted as \`previsit_forms.notificationSchemeIdOverride\` and consulted by \`getScheme()\` at send time.

### **3. Dynamic Parameters**
- **Description:** A single key/value map (\`config.dynamicParams\`) that is substituted into:
   - **Form field labels** when the form is rendered to the patient.
   - **Notification scheme content** when outreach is sent for this form set (any \`{{key}}\` in the scheme that isn't a built-in modality param).

  When a notification scheme override is selected, the editor splits the section into two:
   - **Notification scheme variables** — auto-populated from the chosen scheme's content. Each row has a read-only key and a required value input. Empty values fail block validation. Values can themselves be upstream block references (e.g. \`{{SomeBlock.data.field}}\`), which are resolved at flow execution time and persisted on the form set.
   - **Additional variables** — free-form key/value rows for form-label substitutions or any other consumer.

- **Steps:**
   1. If a scheme override is set, fill in every required scheme variable.
   2. Add additional variables for form-label substitution as needed.
   3. At send time, built-in modality params (\`patientFirstName\`, \`link\`, ...) always win on key collisions with dynamic params, so dynamic params cannot spoof built-ins.

### **4. Configuration Options**  

#### **Override Entity:**  
- Toggle this option to On if the form set should be attached to an entity other than the one that started the flow.  
- When enabled, **Entity Type** (e.g., patient, encounter) and **Entity Id** (a template expression that resolves at run time) are both required.

#### **Scheduling Configurations:**  
- Toggle this option (default Off) if you need to tie form delivery to specific scheduling rules.

#### **Run Auto Export:**  
- Toggle this option to On if you want the form responses to automatically trigger export actions such as exporting forms and patient information once the form set reaches a terminal state.

#### **Mode:**  
- Select how the block coordinates with the form set's lifecycle:  
   - **No wait (previously _Add forms only_) (\`AddOnly\`):** the block completes as soon as the form set is added. The flow continues immediately and does not observe the form set's terminal status.  
   - **Wait for formset to reach any terminal status (\`AddAndWait\`):** the block adds the form set and then waits up to the configured timeout for the form set to reach a terminal state (Completed or Expired). When a terminal state is reached the block continues regardless of whether the form set was completed or expired.  
   - **Wait for formset to be completed (\`AddAndWaitForCompletion\`, default):** the block adds the form set and waits for a terminal state, but **errors** the block unless the form set was Completed. Use this when an incomplete (expired) form set should stop the flow rather than let it proceed.

#### **Timeout:**  
- Visible and **required** when **Mode** is either **Wait for formset to reach any terminal status** or **Wait for formset to be completed**.
- Enter a positive integer and select from available duration units (minutes, hours, days).  
- What the timeout waits for (and the field label) depends on the mode:  
   - In **Wait for formset to reach any terminal status** mode it is labeled **Form Set Terminal State Timeout** — the window for the form set to reach any terminal state (Completed or Expired).  
   - In **Wait for formset to be completed** mode it is labeled **Form Set Completion Timeout** — the window for the form set to be Completed.
- In either mode, if the timeout fires before the awaited state is reached, the block **errors** (\`timedOut: true\` is still recorded on the block output). The single output handle means an unhandled timeout fails the flow run unless an error edge is wired from the block.

### **5. Save Your Configuration**  
Once you have completed the selections and input all required dynamic parameters and options, save your configuration to update the flow.

### **6. Form Template Configuration (per template)**  

#### **Form Template ID:**  
- This is a unique identifier for the selected template.

#### **Refresh Interval (optional):**  
- Specifies how frequently the same form can be re-attached for the patient after the last completion.  
- Enter a positive integer and select from available duration units (minutes, hours, days).

## How to Test

### **1. Execute a Test Flow**  
- Run your flow with test patient data to ensure the selected form(s) are correctly attached, dynamic parameters are replaced accurately, and the override entity (if any) is used.

### **2. Verify Mode Behavior**  
- In **No wait** mode, confirm that the block completes immediately after the form set is created.
- In **Wait for formset to reach any terminal status** mode, complete (or expire) the form set in a test environment and verify that the block resumes with the expected \`formSet.status\` and \`timedOut: false\`. Try a short timeout to verify that, when the form set has not reached a terminal state, the block now **errors** (recording \`timedOut: true\`) instead of continuing.  
- In **Wait for formset to be completed** mode, verify that completing the form set lets the flow continue, while an expired form set (or a timeout) **errors** the block.

### **3. Review Export Actions**  
- If Run Auto Export is enabled, verify that the form responses trigger the appropriate export actions.

### **4. Validate UI Behavior**  
- Ensure that any errors (e.g., missing form template, missing timeout in \`AddAndWait\`, missing entity override fields) appear as expected and that the dynamic parameters table functions correctly (e.g., adding/removing rows).

## Best Practices

1. **Select Valid Form Templates:**  
   Use the provided search field to select forms that are well-tested and contain all required fields.

2. **Use Dynamic Parameters Effectively:**  
   Map keys and values clearly to ensure that patient-specific or context-specific data is accurately inserted into the forms.

3. **Pick the smallest reasonable timeout.**  
   A long timeout keeps flow runs and resources in flight; choose a value that matches the patient's realistic completion window.

4. **Use \`AddOnly\` when downstream blocks don't depend on the form result.**  
   This keeps the flow simple and avoids unnecessary waiting.

5. **Monitor Refresh Intervals:**  
   Configure refresh intervals thoughtfully to avoid resending the same form too frequently.

6. **Document Configuration:**  
   Keep records of which forms and dynamic parameters are used, so that troubleshooting and later updates are straightforward.

### Example Output

When **Mode** is \`AddAndWait\` and any terminal state is reached (Completed or Expired), the block returns the form set (with \`formSet.status\` updated to the terminal status it observed) plus a \`timedOut: false\` boolean. In \`AddAndWaitForCompletion\` mode the block only returns this shape when the form set is Completed:

~~~json
{
   "formSet": {
      "id": "00000000-0000-0000-0000-000000000000",
      "status": "FORM_SET_COMPLETED",
      "created": "2025-03-27T16:57:10.502Z",
      "expires": "2025-03-30T16:57:10.502Z",
      "updated": "2025-03-27T16:57:10.502Z",
      "encounterId": null,
      "authBypassCode": null,
      "previsitLinkId": null,
      "authCodeExpires": "2025-03-30T16:57:10.502Z",
      "closedTimestamp": "2025-03-27T17:42:11.117Z",
      "ehrInformationId": "0195d373-c6d4-78cd-bae4-aa5d33906958",
      "authenticationCode": "00000000",
      "currentStateExported": false
   },
   "timedOut": false
}
~~~

If the timeout fires before the form set reaches a terminal state (or completion, in \`AddAndWaitForCompletion\` mode), the block errors (recording \`timedOut: true\` with \`formSet.status\` at its latest non-terminal value, e.g. \`CREATED\`). In \`AddAndWaitForCompletion\` mode, reaching a non-completed terminal state (e.g. \`FORM_SET_EXPIRED\`) also errors the block.

When **Mode** is \`AddOnly\`, the block returns only the \`formSet\` (in its initial \`CREATED\` state) and no \`timedOut\` flag.
`;
